import { CircularProgress, Grid, Typography } from '@mui/material';
import { useSignals } from '@preact/signals-react/runtime';
import { useMutation } from '@tanstack/react-query';
import { filter, includes, isEmpty } from 'es-toolkit/compat';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';

import { runSecondaryAnalysis as runSecondaryAnalysisApiCall } from 'api/platform';
import { geojsonFeaturesToMultiPolygon } from 'components/Procedure/Infobar/SlideInfobar/Results/SecondaryAnalysisTrigger';
import { JobType, SecondaryAnalysisInput } from 'interfaces/job';
import {
  secondaryAnalysisExclusionModes,
  secondaryAnalysisInclusionModes,
} from 'services/secondaryAnalysis/secondaryAnalysisSelectionModes';
import queryClient from 'utils/queryClient';
import { SecondaryAnalysisContext } from '.';
import { secondaryAnalysisPolygons } from './secondaryAnalysisPolygons';
import { useClearSecondaryAnalysisSelections } from './useClearSecondaryAnalysisSelections';

export const SNACK_BAR_KEY_SECONDARY_ANALYSIS = 'secondary-analysis';

export const useRunSecondaryAnalysis = ({ onSuccess }: { onSuccess: () => void }) => {
  useSignals();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const clearSecondaryAnalysisSelections = useClearSecondaryAnalysisSelections();

  const runSecondaryAnalysisMutation = useMutation(runSecondaryAnalysisApiCall, {
    mutationKey: ['runSecondaryAnalysis'],
    onError: () => {
      enqueueSnackbar('Error occurred, Calculate Features failed', { variant: 'error' });
    },
    onSuccess: () => {
      enqueueSnackbar('Calculate Features Started', { variant: 'success' });
      // Invalidate jobs query to get the new job
      queryClient.invalidateQueries(['jobs']);
      clearSecondaryAnalysisSelections(true);
      onSuccess?.();
    },
    onSettled() {
      // Invalidate jobs query to get the new job
      queryClient.invalidateQueries(['jobs']);
      closeSnackbar(SNACK_BAR_KEY_SECONDARY_ANALYSIS);
    },
  });
  const runSecondaryAnalysis = useCallback(
    (
      {
        secondaryAnalysisActiveViewer,
        caseId,
        slideId,
        studyId,
        secondaryAnalysisSourceOrchestrationId,
        autoInternallyApprove,
        wholeSlideAnalysisJobName,
        wholeSlideAnalysisJobDescription,
        wholeSlideAnalysisJobType,
        wholeSlideAnalysisJobParams,
        secondaryAnalysisNotifications,
      }: SecondaryAnalysisContext,
      debug: boolean = false
    ) => {
      const secondaryAnalysisFeature = secondaryAnalysisPolygons[secondaryAnalysisActiveViewer].value;

      const secondaryAnalysisFeatures = filter(
        secondaryAnalysisFeature?.features,
        (feature) => feature.properties.orchestrationId === secondaryAnalysisSourceOrchestrationId
      );
      const inclusionFeatures = filter(secondaryAnalysisFeatures, (feature) =>
        includes(secondaryAnalysisInclusionModes, feature.properties.featureType)
      );

      const exclusionFeatures = filter(secondaryAnalysisFeatures, (feature) =>
        includes(secondaryAnalysisExclusionModes, feature.properties.featureType)
      );

      if (
        wholeSlideAnalysisJobType !== JobType.PostProcessingAndCalculateFeatures &&
        wholeSlideAnalysisJobType !== JobType.NucleaiExecutor
      ) {
        console.error(
          'Whole slide analysis job is not a CalculateFeatures or a study assigned algorithm with final results',
          wholeSlideAnalysisJobType
        );
        enqueueSnackbar('Error occurred, Calculate Features failed', { variant: 'error' });
        return;
      }

      if (debug) {
        console.debug('Running secondary analysis with params:', {
          caseId,
          slideId,
          studyId,
          secondaryAnalysisActiveViewer,
          secondaryAnalysisSourceOrchestrationId,
          autoInternallyApprove,
          inclusionFeatures,
          exclusionFeatures,
          wholeSlideAnalysisJobName,
          wholeSlideAnalysisJobDescription,
          wholeSlideAnalysisJobType,
          wholeSlideAnalysisJobParams,
          secondaryAnalysisNotifications,
        });
      }

      if (isEmpty(wholeSlideAnalysisJobParams)) {
        console.error('Whole slide analysis job params not found', wholeSlideAnalysisJobParams);
        enqueueSnackbar('Error occurred, Calculate Features failed', { variant: 'error' });
        return;
      }

      if (wholeSlideAnalysisJobParams?.primaryRunOrchestrationId) {
        console.error(
          'Whole slide analysis job has a primary job - implying it is a secondary analysis itself',
          wholeSlideAnalysisJobParams
        );
        enqueueSnackbar('Error occurred, Calculate Features failed', { variant: 'error' });
        return;
      }

      const secondaryAnalysisPolygonsToSend: SecondaryAnalysisInput = {
        [caseId]: {
          [slideId]: {
            ...(!isEmpty(inclusionFeatures)
              ? { inclusionMultipolygon: geojsonFeaturesToMultiPolygon(inclusionFeatures) }
              : {}),
            ...(!isEmpty(exclusionFeatures)
              ? { exclusionMultipolygon: geojsonFeaturesToMultiPolygon(exclusionFeatures) }
              : {}),
          },
        },
      };

      const runTime = moment().format('YYYY-MM-DD HH:mm:ss');

      const mutateParams = {
        jobName: `${wholeSlideAnalysisJobName} - Secondary Analysis - ${runTime}`,
        jobDescription: `${wholeSlideAnalysisJobDescription} - Secondary Analysis - ${runTime}`,
        notifications: secondaryAnalysisNotifications,
        params: {
          studyId,
          manifest: { [caseId]: [slideId] },
          primaryRunOrchestrationId: secondaryAnalysisSourceOrchestrationId,
          secondaryAnalysisPolygons: secondaryAnalysisPolygonsToSend,
        },
      };

      if (debug) {
        console.debug('Secondary Analysis mutateParams:', mutateParams);
        return;
      }

      runSecondaryAnalysisMutation.mutate(mutateParams);

      enqueueSnackbar({
        variant: 'success',
        message: (
          <Grid container>
            <Grid item>
              <Typography>Waiting for Secondary Analysis to start</Typography>
            </Grid>
            <Grid item>
              <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
            </Grid>
          </Grid>
        ),
        key: SNACK_BAR_KEY_SECONDARY_ANALYSIS,
        autoHideDuration: null,
      });
    },
    [enqueueSnackbar, runSecondaryAnalysisMutation, enqueueSnackbar, clearSecondaryAnalysisSelections]
  );

  return {
    runSecondaryAnalysis,
    isStartingSecondaryAnalysis: runSecondaryAnalysisMutation.isLoading,
  };
};
